import { Inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { LogService } from "./log.service";
import {
  APIResponse,
  CacheNamesEnum,
  DomainDTO,
  DomainRegisterDateDTO,
  FreeServiceResult,
  HttpErrorsEnum,
  PersonalTrustedDomains
} from "../models/shared.model";
import { DateUtils } from "../utils/date.utils";
import { MailUtils } from "../utils/mail.utils";
import { StringUtils } from "../utils/string.utils";
import { environment } from "../../environments/environment";
import { Subject } from "rxjs";
import moment from "moment/moment";
import { DOMAIN_CONFIG_KEYS } from "../models/constants";

@Injectable()
export class DomainService {
  private baseUrl: string;

  public reloadPersonalTrustedList: Subject<boolean> = new Subject();

  constructor(
    @Inject(HttpClient) private http: HttpClient,
    @Inject(LogService) private log: LogService
  ) {
    this.baseUrl = environment.backendUrl;
  }

  async getDomainRegisteredDate(domain: string): Promise<Date | null> {
    return new Promise<Date | null>(resolve => {
      this.http.get(this.baseUrl + `/external/whois?domain=${domain}`).subscribe(
        (res: APIResponse<DomainRegisterDateDTO>) => {
          if (res.ok)
            resolve(res.data.created_at);
          resolve(null);
        },
        (error: any) => {
          console.error(error);
          this.log.logException(new Error(`Error analyzing domain age - ${JSON.stringify(error)}`));
          resolve(null);
        }
      );
    });
  }

  async checkInternalEmail() {
    const receiverDomain: string = MailUtils.parseMultiLevelDomain(Office.context.mailbox.userProfile.emailAddress);
    const senderDomain: string = MailUtils.parseMultiLevelDomain(Office.context.mailbox.item.sender.emailAddress);

    const freeEmailServices = this.getFreeEmailServices();

    let isFreeService = freeEmailServices.some(e =>
      e.domain === receiverDomain.toLowerCase() || e.domain === senderDomain.toLowerCase()
    );

    return StringUtils.compareCaseInsensitive(receiverDomain, senderDomain) && !isFreeService;
  }

  calculateDomainAge(registeredDate: Date) {

    const formattedDate: string = `${moment(registeredDate).format("MMM yyyy")}`;

    const months = DateUtils.getDateAgeInMonths(registeredDate);

    return { formattedDate, months };
  }

  getDomains(isTopLevel = false, isTrusted = true): DomainDTO[] {

    let type = CacheNamesEnum.TRUSTED_DOMAINS;

    if (isTopLevel) {
      type = isTrusted ? CacheNamesEnum.TRUSTED_TOP_LEVEL_DOMAINS : CacheNamesEnum.DANGEROUS_TOP_LEVEL_DOMAINS;
    }

    return JSON.parse(window.localStorage.getItem(type)!) as DomainDTO[];
  }

  getFreeEmailServices(): FreeServiceResult[] {
    return JSON.parse(window.localStorage.getItem(CacheNamesEnum.FREE_EMAIL_SERVICE)!) as FreeServiceResult[];
  }

  async getBlackListDomains(): Promise<string[]> {
    return new Promise(resolve => {

      let list = JSON.parse(window.localStorage.getItem(CacheNamesEnum.BLACKLIST_DOMAINS)!) as string[];

      if (!list || list.length == 0) {
        list = ["example.test", "test.xyz"];
        list = list.map(e => e.toLowerCase());

        window.localStorage.setItem(CacheNamesEnum.BLACKLIST_DOMAINS, JSON.stringify(list));
      }

      list.map((e: any) => {
        e.toLowerCase();
      });

      resolve(list);
    });
  }

  checkTLDCategory(tld: string) {
    const trustedTld = this.getDomains(true, true);
    const dangerousTld = this.getDomains(true, false);

    if (trustedTld.find(value => StringUtils.compareCaseInsensitive(value.domain, tld)))
      return true;

    if (dangerousTld.find(value => StringUtils.compareCaseInsensitive(value.domain, tld)))
      return false;

    return null;
  }

  async fetchPersonalTrustedList(){
    return new Promise<PersonalTrustedDomains | null>(resolve => {
      this.http.get(this.baseUrl + `/user/trusted`).subscribe({
        next(result: APIResponse<PersonalTrustedDomains>) {
          if(result.ok) {
            window.localStorage.setItem(CacheNamesEnum.PERSONAL_TRUSTED_DOMAINS, JSON.stringify(result.data));
            resolve(result.data)
          }
          resolve(null);
        },
        error: () => {

        }
      });
    });
  }

  loadPersonalTrustedListFromCache(): PersonalTrustedDomains{
    let trustedDomains = JSON.parse(localStorage.getItem(CacheNamesEnum.PERSONAL_TRUSTED_DOMAINS));
    trustedDomains.domains = new Map(Object.entries(trustedDomains.domains));
    trustedDomains.emails = new Map(Object.entries(trustedDomains.emails));

    return trustedDomains;
  }

  async addDomainToTrustedList(domain: DomainRegisterDateDTO): Promise<APIResponse<void>> {
    const personalTrusted = this.loadPersonalTrustedListFromCache()
    const limit = personalTrusted.limits?.user
    const currentCount = personalTrusted.domains.size + personalTrusted.emails.size;

    return new Promise<APIResponse<void>>((resolve, reject) => {
      if(currentCount == limit)
        return reject({
          ok: false,
          error: HttpErrorsEnum.TRUSTED_LIMIT
        });

      let url = this.baseUrl + `/user/trust/${domain.domain_name}`;

      if (domain.company_name)
        url += `?company=${domain.company_name}`;

      this.http.post(url, {}).subscribe({
        next: (res: APIResponse<void>) => {
          resolve(res);
        },
        error: (error) => {
          console.error("Failed to add domain to trusted list");
          console.error(error);
          reject(null);
        }
      });
    });
  }

  untrustDomain(domain: string, isDomain: boolean): Promise<APIResponse<void>> {
    return new Promise<APIResponse<void>>((resolve, reject) => {
      this.http.post(this.baseUrl + `/user/untrust/${domain}`, {}).subscribe({
        next: (res: APIResponse<void>) => {
          this.removeTrustedDomainFromCache(domain.toLowerCase(), isDomain);
          resolve(res);
        },
        error: (error) => {
          console.error("Failed to remove domain from trusted list");
          console.error(error);
          reject(null);
        }
      });
    });
  }

  removeTrustedDomainFromCache(domain: string, isDomain: boolean) {
    let trustedDomains = this.loadPersonalTrustedListFromCache();

    if(isDomain)
      trustedDomains.domains.delete(domain)
    else
      trustedDomains.emails.delete(domain)

    window.localStorage.setItem(CacheNamesEnum.PERSONAL_TRUSTED_DOMAINS, JSON.stringify({
      domains: Object.fromEntries(trustedDomains.domains),
      emails: Object.fromEntries(trustedDomains.emails),
    }));
  }

  checkDomainConfig(domainName: string, domainConfig: DOMAIN_CONFIG_KEYS) {
    let domainDto = this.getDomains().find(value => value.domain == domainName);
    if(domainDto)
      return domainDto[domainConfig.toString().toLowerCase()];

    let personalDomainDto = this.loadPersonalTrustedListFromCache().domains.get(domainName)
    if(personalDomainDto)
      return personalDomainDto[domainConfig.toString().toLowerCase()] ? true : undefined;

    return undefined;
  }

  async updateDkimRecords(domain: string): Promise<void> {
    const body = {
      enabled: [domain]
    };
  
    return new Promise((resolve, reject) => {
      this.http.post(this.baseUrl + `/mail/dkim`, body)
        .subscribe({
          next: () => {
            resolve();
          },
          error: (error) => {
            console.error(`Failed to add ${domain} to DKIM enabled list`, error);
            reject(error);
          }
        });
    });
  }
}
